'use client';

import React, { FC, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { getCookie } from 'cookies-next';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { getCookieZipCode } from '@/components/customercenter-ui/utils/cookieUtils';
import {
  addZipToCookie,
  createFedexRequestPayload,
  getArrivalDateFromResponse,
  getDefaultDeliveryDate,
  getDeliverydateFromStorage,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { useProductDetails } from '@/context/productDetailProvider';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { GetEstimatedDeliveryDatePayload } from '@/sdk/composable-commerce/types/payloads/GetEstimatedDeliveryDatePayload';
import { useProduct } from 'frontastic';
import { ProductContext } from './contexts';
import { getFilteredAttribute } from './helpers/utils/productDataUtils';
import { COOKIE_KEYS } from '../../../../../constants/constant';
import { getCurrentCentralTime, maskPostalCode } from '../../utils/formatUtils';
import ProductDetails from '../details/components/productDetails';
export interface ProductDetailsProps {
  product: Product | any;
  widgetData: any;
  colorsForBadges: any;
}

const PdpPage: FC<ProductDetailsProps> = ({ product, widgetData }) => {
  const pathname = usePathname();
  let urlSKU = pathname.substring(pathname.lastIndexOf('/') + 1);
  const variantKeyBreak = urlSKU?.substring(0, urlSKU?.lastIndexOf('-') + 4);
  const [productData, setProductData] = useState<any>();
  urlSKU = urlSKU?.replace(/([0-9]+-[0-9]+)(-[0-9]).*/, '$1');
  const [selectedColor, setSelectedColor] = useState();
  const [selectedSize, setSelectedSize] = useState();
  const [globalAddToCart, setGlobalAddToCart] = useState();
  const [globalSizeAndWidthGroup, setGlobalSizeAndWidthGroup] = useState<any>();
  const [isMailOrderProduct, setIsMailOrderProduct] = useState(false);
  const [isCompleteOutOfStock, setIsCompleteOutOfStock] = useState<boolean>(false);
  const [globalPersonalize, setGlobalPersonalize] = useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCreditCustomer, setIsCreditCustomer] = useState<boolean>(true);
  const [webAccountId, setWebAccountId] = useState<string>('');
  const [categoryName, setCategoryName] = useState('');
  const [isPersonalizeEligible, setIsPersonalizeEligible] = useState<boolean>(false);
  const [isPersonalizeMandatory, setIsPersonalizeMandatory] = useState<boolean>(false);
  const [isPersonalizeOptionSelected, setIsPersonalizeOptionSelected] = useState<boolean>(false);

  const [isProductDataFetched, setIsProductDataFetched] = useState(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const { productData: pData, loading } = useProductDetails();

  useEffect(() => {
    setIsMailOrderProduct(product?.productId === '999999');
  }, [product]);

  const { updateProductRules, getEstimatedDeliveryDate } = useProduct();
  const [skuToRulesObject, setSkuToRulesObject] = useState<{ [key: string]: string[] }>({});
  const [initialArrivalDate, setInitialArrivalDate] = useState('');

  useEffect(() => {
    const fetchProductRuleData = async () => {
      let productId: string;
      if (selectedColor && typeof selectedColor === 'object' && 'key' in selectedColor) {
        productId = (selectedColor as { key: string }).key;
      } else {
        productId = product?.productId || '';
      }
      if (isProductDataFetched) {
        const productData: any = await updateProductRules({
          productId: productId,
        });
        setSkuToRulesObject(productData?.data || {});
      }
    };
    fetchProductRuleData();
  }, [isProductDataFetched, selectedColor]);

  const setOutOfStockProduct = (productData: any) => {
    const isOutOfStock =
      productData?.data?.products?.results?.every((element: any) => {
        const allVariants: Variant[] = [element?.masterVariant, ...(element?.variants || [])].filter(Boolean);

        return allVariants?.every((variantItem) => !(variantItem?.isAvailable || variantItem?.backOrder));
      }) ?? false;
    setIsCompleteOutOfStock(isOutOfStock);
  };

  useEffect(() => {
    if (productData?.results?.[0]?.masterVariant) {
      const isPersonalizedEligible =
        getFilteredAttribute('personalizeOption', productData.results?.[0]?.masterVariant) ?? false;
      const isPersonalizeMandatory =
        getFilteredAttribute('personalizeMandatory', productData.results?.[0]?.masterVariant) ?? false;
      setIsPersonalizeEligible(isPersonalizedEligible);
      setIsPersonalizeMandatory(isPersonalizeMandatory);
      setIsPersonalizeOptionSelected(isPersonalizedEligible);
      if (isPersonalizedEligible) {
        try {
          const cookieValue = getCookie(COOKIE_KEYS.CDP_URL ?? '') ?? '';
          const data = JSON.parse(cookieValue);
          setWebAccountId(data?.omsCustomerId ?? data?.customerId ?? '');
        } catch (error) {
          console.error('Error while parsing json', error);
        }
      }
    }
  }, [productData]);

  useEffect(() => {
    const fetchProductGroupData = async () => {
      processProductData(pData);
    };
    if (!loading && pData) {
      fetchProductGroupData();
    }
  }, [loading, pData]);

  const processProductData = (productData: any) => {
    setProductData(productData?.data?.products);
    setIsProductDataFetched(true);
    setSelectedColor(productData?.data?.products?.results?.[0]);
    productData?.data?.products?.results.map((obj: any) => {
      if (obj.key === urlSKU) {
        if (variantKeyBreak !== urlSKU) {
          if (obj?.masterVariant?.sku === variantKeyBreak) {
            setSelectedSize(obj?.masterVariant);
          }
          obj?.variants?.forEach((subObj: any) => {
            if (subObj?.sku === variantKeyBreak) {
              setSelectedSize(subObj);
            }
          });
        }
        setSelectedColor(obj);
      }
    });

    if (productData && product?.productId) {
      setOutOfStockProduct(productData);
    }
  };

  const getArrivalDate = async (zipCode: string, pageName?: string): Promise<string> => {
    let arrivalDateValue = '';
    if (zipCode) {
      const payload: GetEstimatedDeliveryDatePayload = createFedexRequestPayload(zipCode, pageName);
      const response: any = await getEstimatedDeliveryDate(payload);
      if (
        (!response?.isError || response?.data?.isSuccess) &&
        response?.data?.fedexResponseDetails?.smartPostDeliveryDate
      ) {
        arrivalDateValue = response?.data?.fedexResponseDetails?.smartPostDeliveryDate;
        addZipToCookie(zipCode);
      } else {
        throw Error('Error while fetching delivery date');
      }
    }
    return arrivalDateValue || '';
  };

  const fetchArrivalDate = async () => {
    try {
      const zipCodeonLoad = getCookieZipCode() || projectSettings?.defaultZipCode || '';
      const deliveryDateDataLocalStorage = getDeliverydateFromStorage(zipCodeonLoad, 'deliveryDateData');
      let deliveryDateValue;
      if (!deliveryDateDataLocalStorage) {
        deliveryDateValue = await getArrivalDate(zipCodeonLoad, mapLocaleToMeaningfulFormat().pdpPage);
        const deliveryDateData = {
          zipCode: zipCodeonLoad,
          deliveryDate: deliveryDateValue,
          isSucess: true,
          date: getCurrentCentralTime(),
        };
        localStorage.setItem('deliveryDateData', JSON.stringify(deliveryDateData));
      } else {
        deliveryDateValue = deliveryDateDataLocalStorage;
      }
      if (deliveryDateValue) {
        const arrivalDateValue = getArrivalDateFromResponse(deliveryDateValue, false, false, projectSettings);
        setInitialArrivalDate(arrivalDateValue);
      }
    } catch (error) {
      const defaultArrivalDate = getDefaultDeliveryDate(
        mapLocaleToMeaningfulFormat().standardShipMethod,
        projectSettings?.defaultDeliveryDates,
        false,
        false,
        projectSettings,
      );
      setInitialArrivalDate(defaultArrivalDate);
      addZipToCookie(maskPostalCode(getCookieZipCode()));
    }
  };

  useEffect(() => {
    if (pData) {
      if (!projectSettings?.isFedexEnabled) {
        const defaultArrivalDate = getDefaultDeliveryDate(
          mapLocaleToMeaningfulFormat().standardShipMethod,
          projectSettings?.defaultDeliveryDates,
          false,
          false,
          projectSettings,
        );
        setInitialArrivalDate(defaultArrivalDate);
      } else {
        fetchArrivalDate();
      }
    }
  }, [pData]);

  return (
    <>
      <Script id="jquery-lib" src="/js/jQuery/jquery-3.7.0.min.js" async />
      <ProductContext.Provider
        value={{
          product: product,
          productData: productData,
          initialColor: selectedColor,
          setInitialColor: setSelectedColor,
          initialSize: selectedSize,
          setInitialSize: setSelectedSize,
          globalAddToCart: globalAddToCart,
          setGlobalAddToCart: setGlobalAddToCart,
          globalPersonalize: globalPersonalize,
          setGlobalPersonalize: setGlobalPersonalize,
          widgetData: widgetData,
          setGlobalSizeAndWidthGroup: setGlobalSizeAndWidthGroup,
          globalSizeAndWidthGroup: globalSizeAndWidthGroup,
          isCreditCustomer: isCreditCustomer,
          webAccountId: webAccountId,
          isCompleteOutOfStock: isCompleteOutOfStock,
          categoryName: categoryName,
          setCategoryName: setCategoryName,
          isPersonalizeEligible: isPersonalizeEligible,
          setIsPersonalizeEligible,
          isPersonalizeMandatory: isPersonalizeMandatory,
          setIsPersonalizeMandatory,
          isPersonalizeOptionSelected: isPersonalizeOptionSelected,
          setIsPersonalizeOptionSelected: setIsPersonalizeOptionSelected,
          isMailOrderProduct: isMailOrderProduct,
          skuToRulesObject: skuToRulesObject,
          getArrivalDate: getArrivalDate,
          initialArrivalDate: initialArrivalDate,
          setInitialArrivalDate: setInitialArrivalDate,
        }}
      >
        <section className="max-w-screen-9xl mx-auto px-4">
          <ProductDetails />
        </section>
      </ProductContext.Provider>
    </>
  );
};

export default PdpPage;
